// ─── Service « Listes partagées » (tâches / courses) ──────────────
// Porté de MoPCal (`services/listes.js`), première coupe : nom + emoji,
// items cochables avec quantité/unité. Rattachée au CALENDRIER (comme
// MoPCal), pas à l'espace — `firestore.rules` porte déjà la règle
// (`accesCalId`, corrigée le 17/08 pour couvrir les calendriers créés dans
// la suite, pas seulement ceux hérités de MoPCal).
// Laissé pour un prochain incrément : droits par personne, photos,
// justificatifs, lien vers un ingrédient du catalogue (feature-meals).
//
//   listes/{id}            { calendrierId, titre, emoji, nbItems, nbFaits,
//                             creePar, creeParNom, creeLe }
//   listes/{id}/items/{id} { texte, quantite, unite, fait, faitPar,
//                             faitParNom, faitLe, creePar, creeLe }
import { db } from '@mop/core';
import {
  collection, doc, addDoc, updateDoc, deleteDoc, getDocs, onSnapshot,
  query, where, writeBatch, increment,
} from 'firebase/firestore';

export const EMOJIS_LISTE = ['📝', '🛒', '🎁', '✈️', '🍽️', '🏫', '🧹', '🔧'];

// `onErreur` remonté — même correction que sur les contacts/le journal/la
// messagerie : un refus de règle donnait « aucune liste » en silence.
export function ecouterListes(calendrierId, callback, onErreur) {
  if (!calendrierId) { callback([]); return () => {}; }
  const q = query(collection(db, 'listes'), where('calendrierId', '==', calendrierId));
  return onSnapshot(q,
    (snap) => {
      const liste = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      liste.sort((a, b) => (a.creeLe || '').localeCompare(b.creeLe || ''));
      callback(liste);
    },
    (e) => { callback([]); onErreur?.(e?.message || String(e)); });
}

export async function creerListe({ calendrierId, titre, emoji, creePar, creeParNom }) {
  const ref = await addDoc(collection(db, 'listes'), {
    calendrierId,
    titre: (titre || '').trim() || 'Liste',
    emoji: emoji || '📝',
    nbItems: 0, nbFaits: 0,
    creePar: creePar || null, creeParNom: creeParNom || '',
    creeLe: new Date().toISOString(),
  });
  return ref.id;
}

export async function renommerListe(listeId, { titre, emoji }) {
  const champs = {};
  if (titre != null) champs.titre = (titre || '').trim() || 'Liste';
  if (emoji != null) champs.emoji = emoji || '📝';
  await updateDoc(doc(db, 'listes', listeId), champs);
}

// Supprime la liste et tous ses items (lot).
export async function supprimerListe(listeId) {
  const itemsSnap = await getDocs(collection(db, 'listes', listeId, 'items'));
  const batch = writeBatch(db);
  itemsSnap.forEach(it => batch.delete(it.ref));
  batch.delete(doc(db, 'listes', listeId));
  await batch.commit();
}

// ── Items ── Tri : non cochés d'abord, puis par date de création.
export function ecouterItems(listeId, callback, onErreur) {
  if (!listeId) { callback([]); return () => {}; }
  return onSnapshot(collection(db, 'listes', listeId, 'items'),
    (snap) => {
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => a.fait === b.fait ? (a.creeLe || '').localeCompare(b.creeLe || '') : (a.fait ? 1 : -1));
      callback(items);
    },
    (e) => { callback([]); onErreur?.(e?.message || String(e)); });
}

export async function ajouterItem(listeId, { texte, quantite, unite, creePar }) {
  const t = (texte || '').trim();
  if (!t) return;
  await addDoc(collection(db, 'listes', listeId, 'items'), {
    texte: t, fait: false, faitPar: null, faitParNom: '', faitLe: null,
    quantite: (quantite || '').trim(), unite: (unite || '').trim(),
    creePar: creePar || null, creeLe: new Date().toISOString(),
  });
  await updateDoc(doc(db, 'listes', listeId), { nbItems: increment(1) });
}

export async function modifierItem(listeId, itemId, champs) {
  const out = {};
  for (const k of Object.keys(champs || {})) if (champs[k] !== undefined) out[k] = champs[k];
  if (Object.keys(out).length === 0) return;
  await updateDoc(doc(db, 'listes', listeId, 'items', itemId), out);
}

// On consigne qui coche et quand ; le compteur `nbFaits` reste à jour pour
// la carte de la liste (évite de recompter tous les items à l'affichage).
export async function toggleItem(listeId, itemId, fait, { uid, nom } = {}) {
  await updateDoc(doc(db, 'listes', listeId, 'items', itemId), fait
    ? { fait: true, faitPar: uid || null, faitParNom: nom || '', faitLe: new Date().toISOString() }
    : { fait: false, faitPar: null, faitParNom: '', faitLe: null });
  await updateDoc(doc(db, 'listes', listeId), { nbFaits: increment(fait ? 1 : -1) });
}

export async function supprimerItem(listeId, itemId, itemFait = false) {
  await deleteDoc(doc(db, 'listes', listeId, 'items', itemId));
  await updateDoc(doc(db, 'listes', listeId), {
    nbItems: increment(-1),
    ...(itemFait ? { nbFaits: increment(-1) } : {}),
  });
}
